import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CornerDownRight, Search } from 'lucide-react';
import { WorldSection } from './WorldPageHeader';
import { itemName, itemPath, mapName } from '../../utils/worldFormat';
import { monsterDrops, routeKindLabel } from '../../utils/worldQueries';
import { monsterItemDrops, qualityLabel, routeQuality } from '../../utils/itemSources';
import { normalize } from '../../utils/text';
import { ItemThumb } from './ItemSprite';
import type { WorldData } from '../../types/world';
const PAGE = 30;

/** Búsqueda inversa: qué ítems puede soltar este monstruo y en qué mapas (route_targets.csv). */
export function MonsterItemDrops({
  w,
  monsterId
}: {
  w: WorldData;
  monsterId: number;
}) {
  const drops = useMemo(() => monsterItemDrops(w, monsterId), [w, monsterId]);
  const legacy = useMemo(() => {
    const known = new Set(drops.map(d => d.itemKey));
    const d = monsterDrops(w, monsterId);
    return d.eventItems.filter(e => e.itemId && !known.has(e.itemId));
  }, [w, monsterId, drops]);
  const [q, setQ] = useState('');
  const [showIndirect, setShowIndirect] = useState(true);
  const [limit, setLimit] = useState(PAGE);
  const active = drops.filter(d => !d.disabled);
  const disabledCount = drops.length - active.length;
  const nq = normalize(q);
  const filtered = active.filter(d => (showIndirect || !d.indirect) && (!nq || normalize(d.name).includes(nq)));
  const indirectCount = active.filter(d => d.indirect).length;
  return <WorldSection id="drops" title="Ítems que puede soltar" count={active.length} data-id="element-1469" data-mp="3bulrgayz4pi4">
      {active.length === 0 && legacy.length === 0 ? <p className="text-gray-400" data-id="element-1470" data-mp="2wwjpg57lcc2z">No hay ítems documentados para este monstruo en las rutas de obtención.</p> : <>
          <p className="mb-4 text-sm text-gray-400" data-id="element-1471" data-mp="1lhmpi09vc2gu">
            Combinaciones posibles según la configuración del servidor. No son drops garantizados.
            {disabledCount > 0 ? ` ${disabledCount} rutas desactivadas no se muestran.` : ''}
          </p>
          {active.length > 8 && <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-1472" data-mp="3o4bgluyrwlwp">
              <label className="relative flex-1" data-id="element-1473" data-mp="3o6nx03ovuo7n">
                <span className="sr-only" data-id="element-1474" data-mp="3jcjpnjbpge61">Filtrar ítems</span>
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden="true" data-id="element-1475" data-mp="3eei5ev2jm8sz" />
                <input value={q} onChange={e => {
            setQ(e.target.value);
            setLimit(PAGE);
          }} placeholder="Filtrar por nombre de ítem…" className="h-11 w-full rounded-full border border-white/15 bg-[#050b14] pl-10 pr-4 text-sm text-white placeholder:text-gray-500 focus:border-[#D4AF37]/60 focus:outline-none" data-id="element-1476" data-mp="39ggreojz4zod" />
              </label>
              {indirectCount > 0 && <label className="flex h-11 items-center gap-2 text-sm text-gray-300" data-id="element-1477" data-mp="tosrqldkst1v">
                  <input type="checkbox" checked={showIndirect} onChange={e => setShowIndirect(e.target.checked)} className="h-4 w-4 accent-[#D4AF37]" data-id="element-1478" data-mp="1170segsjghcd" />
                  Incluir obtención indirecta ({indirectCount})
                </label>}
            </div>}
          <ul className="grid gap-2 md:grid-cols-2" data-id="element-1479" data-mp="1fvn4m9mvcszd">
            {filtered.slice(0, limit).map(d => {
          const routes = d.routes.filter(r => !r.disabled);
          const maps = Array.from(new Set(routes.flatMap(r => r.mapIds)));
          const kinds = Array.from(new Set(routes.map(r => r.indirect || r.route.viaItemIds.length ? 'Al abrir una caja o bolsa' : r.eventIds.some(e => e.startsWith('invasion:')) ? 'Recompensa de invasión' : r.eventIds.length ? 'Premio de evento' : r.route.kind === 'quest' ? 'Material de misión' : r.route.kind === 'natural' || r.route.kind === 'bag' || r.route.kind === 'rule' ? 'Drop del monstruo' : routeKindLabel[r.route.kind] ?? r.route.kind).map((label, i) => routeQuality(routes[i].route) === 'normal' ? label : `${label} (${qualityLabel[routeQuality(routes[i].route)]})`)));
          const via = Array.from(new Set(routes.filter(r => r.indirect).flatMap(r => r.route.viaItemIds))).map(id => itemName(w, id));
          return <li key={d.itemKey} className="flex gap-3 rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-1480" data-mp="2tvron552w5qn">
                  <ItemThumb id={d.itemKey} name={d.name} data-id="element-1481" data-mp="38uloksdj6sz8" />
                  <div className="min-w-0" data-id="element-1482" data-mp="33uftf281yoce">
                  <Link to={`${itemPath(d.itemKey)}#donde-conseguirlo`} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1483" data-mp="33vihl2dzqcom">
                    {d.name}
                  </Link>
                  <p className="mt-1 text-xs text-gray-400" data-id="element-1484" data-mp="3rfqfdaawd0ou">
                    {kinds.join(' · ')}
                    {maps.length ? ` · ${maps.map(m => mapName(w, m)).join(', ')}` : ''}
                  </p>
                  {d.indirect && <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-100" data-id="element-1485" data-mp="33s04h54x2128">
                      <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1486" data-mp="38j8h0a1jm2jm" />
                      {via.length ? `Indirecto: entrega ${via.join(' o ')}, que puede contenerlo.` : 'Indirecto: no lo entrega directamente.'}
                    </p>}
                  </div>
                </li>;
        })}
            {legacy.map(e => <li key={e.id} className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-1487" data-mp="2pt0h00t53s7n">
                <Link to={itemPath(e.itemId ?? '')} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-1488" data-mp="26odv26makdep">
                  {itemName(w, e.itemId)}
                </Link>
                {e.condition && <p className="mt-1 text-xs text-gray-400" data-id="element-1489" data-mp="1wykm7ddjace5">{e.condition}</p>}
              </li>)}
          </ul>
          {filtered.length === 0 && <p className="text-sm text-gray-400" data-id="element-1490" data-mp="2pmgco0cif82h">Ningún ítem coincide con el filtro.</p>}
          {filtered.length > limit && <button type="button" onClick={() => setLimit(l => l + PAGE)} className="mt-3 h-11 rounded-full border border-white/15 px-5 text-sm font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-1491" data-mp="2koonhe39201z">
              Ver más ({filtered.length - limit} restantes)
            </button>}
        </>}
    </WorldSection>;
}